// Formula rules of sculpt-api `docs/FORMULA_RULES.md`, as a lint for the writes this add-in runs.
// Floor: Excel 2016 perpetual. Pure: no Office.js.
//
// A function is either ALLOWED, JUSTIFY_REQUIRED (accepted only with a written justification — the
// block writer adds a note on the label cell) or forbidden (FORBIDDEN_BY_VERSION gets the precise
// "not available in Excel 2016" message, anything else "not in the allow-list").
//
// The lists and limits below are the built-in ones: a copy of what sculpt-api serves (`GET /agent-v2/formula-rules`,
// `sculpt/formulas/rules.py` `as_dict`, version 2026-09-25). The served rules replace them once the session is open
// (`applyFormulaRules`), so the add-in refuses what that server refuses, in its words; the copy is what a session
// that could not fetch them lints with.

export const ALLOWED = new Set([
  'ABS', 'AGGREGATE', 'AND', 'AVERAGE', 'AVERAGEIF', 'AVERAGEIFS', 'CEILING', 'CHOOSE', 'COLUMN', 'COLUMNS',
  'CONCATENATE', 'COUNT', 'COUNTA', 'COUNTBLANK', 'COUNTIF', 'COUNTIFS', 'DATE', 'DATEDIF', 'DAY', 'DAYS',
  'DAYS360', 'EDATE', 'EFFECT', 'EOMONTH', 'EXP', 'FALSE', 'FIND', 'FLOOR', 'FV', 'HLOOKUP', 'IF', 'IFNA', 'INDEX',
  'INT', 'IPMT', 'IRR', 'ISBLANK', 'ISERR', 'ISERROR', 'ISLOGICAL', 'ISNA', 'ISNUMBER', 'ISTEXT', 'LARGE', 'LEFT',
  'LEN', 'LN', 'LOG', 'LOWER', 'MATCH', 'MAX', 'MEDIAN', 'MID', 'MIN', 'MIRR', 'MOD', 'MONTH', 'MROUND', 'N',
  'NETWORKDAYS', 'NOMINAL', 'NORM.S.INV', 'NOT', 'NPER', 'NPV', 'OR', 'PERCENTILE.INC', 'PMT', 'POWER', 'PPMT',
  'PRODUCT', 'PV', 'QUARTILE.INC', 'RANK.EQ', 'RATE', 'RIGHT', 'ROUND', 'ROUNDDOWN', 'ROUNDUP', 'ROW', 'ROWS',
  'SEARCH', 'SIGN', 'SMALL', 'SQRT', 'STDEV.S', 'SUBSTITUTE', 'SUBTOTAL', 'SUM', 'SUMIF', 'SUMIFS', 'SUMPRODUCT',
  'TEXT', 'TRIM', 'TRUE', 'TRUNC', 'UPPER', 'VALUE', 'VLOOKUP', 'WEEKDAY', 'WORKDAY', 'XIRR', 'XNPV', 'YEAR',
  'YEARFRAC',
]);
export const JUSTIFY_REQUIRED = new Set([
  'IFERROR', 'INDIRECT', 'NOW', 'OFFSET', 'RAND', 'RANDBETWEEN', 'TODAY',
]);
export const FORBIDDEN_BY_VERSION = new Set([
  'ARRAYTOTEXT', 'BYCOL', 'BYROW', 'CHOOSECOLS', 'CHOOSEROWS', 'CONCAT', 'DROP', 'EXPAND', 'FILTER', 'GROUPBY',
  'HSTACK', 'IFS', 'IMAGE', 'ISOMITTED', 'LAMBDA', 'LET', 'MAKEARRAY', 'MAP', 'MAXIFS', 'MINIFS', 'PERCENTOF',
  'PIVOTBY', 'RANDARRAY', 'REDUCE', 'REGEXEXTRACT', 'REGEXREPLACE', 'REGEXTEST', 'SCAN', 'SEQUENCE', 'SORT',
  'SORTBY', 'STOCKHISTORY', 'SWITCH', 'TAKE', 'TEXTAFTER', 'TEXTBEFORE', 'TEXTJOIN', 'TEXTSPLIT', 'TOCOL', 'TOROW',
  'UNIQUE', 'VALUETOTEXT', 'VSTACK', 'WRAPCOLS', 'WRAPROWS', 'XLOOKUP', 'XMATCH',
]);

/** Literals that never count as a hard-code in a calculation row (F15). */
export const BENIGN_LITERALS = new Set([0, 1, -1]);
/** Small integer arguments of these functions are positional / rounding parameters, not assumptions. */
export const BENIGN_ARG_FUNCS = new Set([
  'AGGREGATE', 'CEILING', 'CHOOSE', 'DATE', 'DAYS360', 'EDATE', 'EOMONTH', 'FIND', 'FLOOR', 'HLOOKUP', 'INDEX',
  'INT', 'LARGE', 'LEFT', 'MATCH', 'MID', 'MOD', 'MROUND', 'N', 'QUARTILE.INC', 'RANK.EQ', 'RIGHT', 'ROUND',
  'ROUNDDOWN', 'ROUNDUP', 'SEARCH', 'SMALL', 'SUBSTITUTE', 'SUBTOTAL', 'TEXT', 'TRUNC', 'VLOOKUP', 'WEEKDAY',
  'YEARFRAC',
]);
export let BENIGN_ARG_MAX = 12;

export let MAX_FORMULA_LENGTH = 120;
export let MAX_NESTING = 3;
export let MAX_IF_DEPTH = 2;
export let PLUS_CHAIN_TERMS = 3;
export let CONVENTIONS_DOC = 'docs/FORMULA_RULES.md §2';

const STRING_RE = /"(?:[^"]|"")*"/g;
const SHEET_RE = /'[^']*'!|[A-Za-z_][A-Za-z_0-9.]*!/g;
const ERROR_RE = /#(?:REF!|DIV\/0!|NAME\?|VALUE!|N\/A|NUM!|NULL!)/gi;
const FUNC_RE = /(?<![A-Za-z0-9_.])([A-Z][A-Z0-9._]*)\s*\(/gi;
const R1C1_TOKEN_RE = /R\[-?\d+\]|C\[-?\d+\]|R\d+|C\d+|\bR\b|\bC\b/g;
const A1_TOKEN_RE = /\$?[A-Z]{1,3}\$?\d+/g;
const IDENT_RE = /[A-Za-z_][A-Za-z_0-9.]*/g;
const NUM_RE = /(?<![\d.])(\d+(?:\.\d+)?|\.\d+)(%?)/g;
const REF_TERM_RE = /^\s*(?:'[^']*'!|[A-Za-z_][A-Za-z_0-9.]*!)?(?:(?:R\[-?\d+\]|R\d+|R)(?:C\[-?\d+\]|C\d+|C)(?::(?:R\[-?\d+\]|R\d+|R)(?:C\[-?\d+\]|C\d+|C))?|\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?)\s*$/;

function body(formula: string): string {
  return formula.replace(ERROR_RE, '').replace(STRING_RE, '').replace(SHEET_RE, '');
}

const blank = (m: string): string => ' '.repeat(m.length);

export function functionsOf(formula: string): string[] {
  const out = new Set<string>();
  for (const m of body(formula).matchAll(FUNC_RE)) out.add(m[1].toUpperCase());
  return [...out].sort();
}

/** Functions of the formula that do not exist in Excel 2016 perpetual. */
export function forbiddenFunctions(formula: string): string[] {
  return functionsOf(formula).filter((f) => FORBIDDEN_BY_VERSION.has(f));
}

/** Functions outside the allow-list that are not the known 2019+/365 ones. */
export function unlistedFunctions(formula: string): string[] {
  return functionsOf(formula).filter((f) => !ALLOWED.has(f) && !JUSTIFY_REQUIRED.has(f) && !FORBIDDEN_BY_VERSION.has(f));
}

/** Functions accepted only with a written justification. */
export function justifyFunctions(formula: string): string[] {
  return functionsOf(formula).filter((f) => JUSTIFY_REQUIRED.has(f));
}

/** Spill (`#`) and implicit-intersection (`@`) operators — dynamic-array Excel only. */
export function spillOperators(formula: string): string[] {
  const b = body(formula);
  const out: string[] = [];
  if (b.includes('#')) out.push("'#' spill operator");
  if (b.includes('@')) out.push("'@' implicit-intersection operator");
  return out;
}

/** Enclosing function names per character position ('' for a plain parenthesis). */
function walk(b: string, visit: (i: number, ch: string, stack: string[]) => void): void {
  const funcsAt = new Map<number, string>();
  for (const m of b.matchAll(FUNC_RE)) funcsAt.set((m.index as number) + m[0].length - 1, m[1].toUpperCase());
  const stack: string[] = [];
  for (let i = 0; i < b.length; i++) {
    const ch = b[i];
    if (ch === '(') {
      stack.push(funcsAt.get(i) ?? '');
      visit(i, ch, stack);
    } else if (ch === ')') {
      visit(i, ch, stack);
      if (stack.length) stack.pop();
    } else visit(i, ch, stack);
  }
}

/** Numbers typed into a formula (R1C1 or A1) that look like assumptions: references, function names,
 *  strings, exponents, the benign set and small positional arguments of BENIGN_ARG_FUNCS excluded. */
export function formulaLiterals(formula: string): number[] {
  const refsMasked = body(formula).replace(R1C1_TOKEN_RE, blank).replace(A1_TOKEN_RE, blank);
  const masked = refsMasked.replace(IDENT_RE, blank);
  const context = new Map<number, string[]>();
  walk(refsMasked, (i, ch, stack) => {
    if (ch !== '(' && ch !== ')') context.set(i, stack.filter((f) => f));
  });
  const out: number[] = [];
  for (const m of masked.matchAll(NUM_RE)) {
    const start = m.index as number;
    const value = Number(m[1]);
    if (!Number.isFinite(value)) continue;
    const prev = start > 0 ? masked[start - 1] : '';
    if (m[2]) {
      out.push(value / 100);
      continue;
    }
    if (prev === '^') continue;
    const isNegative = prev === '-' && (start < 2 || '(,=+*/<>^ '.includes(masked[start - 2]));
    const signed = isNegative ? -value : value;
    if (BENIGN_LITERALS.has(signed)) continue;
    const enclosing = context.get(start) ?? [];
    const current = enclosing[enclosing.length - 1];
    if (current && BENIGN_ARG_FUNCS.has(current) && Number.isInteger(value) && Math.abs(value) <= BENIGN_ARG_MAX) continue;
    out.push(signed);
  }
  return out;
}

/** Part C rules 8 and 9: length, function nesting, nested IF depth. */
export function complexityProblems(formula: string): string[] {
  const problems: string[] = [];
  if (formula.length > MAX_FORMULA_LENGTH) problems.push(`${formula.length} characters (max ${MAX_FORMULA_LENGTH}) — split it into helper rows above the main row`);
  let maxDepth = 0;
  let maxIf = 0;
  walk(body(formula), (_i, ch, stack) => {
    if (ch !== '(') return;
    const named = stack.filter((f) => f);
    maxDepth = Math.max(maxDepth, named.length);
    maxIf = Math.max(maxIf, named.filter((f) => f === 'IF').length);
  });
  if (maxDepth > MAX_NESTING) problems.push(`${maxDepth} nested function levels (max ${MAX_NESTING}) — split it into helper rows`);
  if (maxIf > MAX_IF_DEPTH) problems.push(`IF nested ${maxIf} deep (max ${MAX_IF_DEPTH}) — use flag rows or helper rows`);
  return problems;
}

/** Number of top-level `+` / `-` terms when every term is a plain reference (0 otherwise). */
export function plusChainTerms(formula: string): number {
  const b = body(formula).replace(/^=/, '');
  const terms: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < b.length; i++) {
    const ch = b[i];
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if ((ch === '+' || ch === '-') && depth === 0 && i > start) {
      terms.push(b.slice(start, i));
      start = i + 1;
    }
  }
  terms.push(b.slice(start));
  const kept = terms.filter((t) => t.trim());
  if (kept.length < PLUS_CHAIN_TERMS || !kept.every((t) => REF_TERM_RE.test(t))) return 0;
  return kept.length;
}

export interface LintOptions {
  /** Typed literals are problems (calc / total rows). */
  calcRow?: boolean;
  justification?: string | null;
  /** This write replaces a formula that is already in those cells, with one more term in it: the rules that
   *  judge a *new* formula — length, nesting depth, nested IFs — do not apply to it.
   *
   *  A project-finance model is full of two-hundred-character consumers written by whoever built it. Wiring a
   *  new revenue line into one of them makes it slightly longer, and the house limit of 120 characters then
   *  refuses the write — mid-batch, after the rest of the plan has landed. One live change ended with its
   *  revenue line missing and two consumers not reading it. The limit is for what we invent, not for what we
   *  extend, and the server sets this flag from the index (the cells already hold a formula), never the model. */
  extendsExisting?: boolean;
  /** This write is the workbook's own formula, copied by an `add` from the object the new one is modelled on: the
   *  allow-list judges what we invent, not the modeller's functions (a chart feeder's NA()). Every other rule still
   *  applies. The server sets it on the copies it builds, never the model (sculpt-api FORMULA_RULES.md F29). */
  copied?: boolean;
}

/** Problems that make a formula unacceptable under the Sculpt conventions. */
export function legacyProblems(formula: string, opts: LintOptions = {}): string[] {
  const problems: string[] = [];
  const bad = forbiddenFunctions(formula);
  if (bad.length) problems.push(`not available in Excel 2016: ${bad.join(', ')} (use INDEX/MATCH, nested IF/CHOOSE, SUMPRODUCT, MAX/MIN…)`);
  const unlisted = unlistedFunctions(formula);
  if (unlisted.length && !opts.copied) problems.push(`not in the allow-list (${CONVENTIONS_DOC}): ${unlisted.join(', ')}`);
  const needs = justifyFunctions(formula);
  if (needs.length && !(opts.justification ?? '').trim()) {
    problems.push(`${needs.join(', ')} allowed only when no legacy alternative exists — pass \`justification\` (why) and a note cell is added`);
  }
  problems.push(...spillOperators(formula));
  if (!opts.extendsExisting) problems.push(...complexityProblems(formula));
  if (opts.calcRow) {
    const lits = formulaLiterals(formula);
    if (lits.length) problems.push(`literal ${lits.join(', ')} typed in a calculation row — put it in an input row and reference it`);
  }
  return problems;
}

/** Advisory findings: justified special functions, `+` chains of references, VLOOKUP / HLOOKUP. */
export function legacyWarnings(formula: string, opts: LintOptions = {}): string[] {
  const warnings: string[] = [];
  const needs = justifyFunctions(formula);
  const j = (opts.justification ?? '').trim();
  if (needs.length && j) warnings.push(`${needs.join(', ')} used with justification: ${j.slice(0, 160)}`);
  const n = plusChainTerms(formula);
  if (n) warnings.push(`${n}-term chain of references — a total belongs in SUM over a contiguous range (a corkscrew closing = opening + additions − reductions is fine)`);
  const lookups = functionsOf(formula).filter((f) => f === 'VLOOKUP' || f === 'HLOOKUP');
  if (lookups.length) warnings.push(`${lookups.join(', ')} allowed — INDEX/MATCH preferred`);
  return warnings;
}

/** Text of the note the block writer puts on the label cell of a justified row. */
export function justificationNote(formula: string, justification: string): string {
  return `Sculpt — ${justifyFunctions(formula).join(', ')} used (no legacy alternative): ${justification.trim()}`;
}

/** The rules as a server serves them (`GET /agent-v2/formula-rules`, sculpt-api `sculpt/formulas/rules.py`). */
export interface FormulaRules {
  version: string;
  doc: string;
  allowed: string[];
  justify_required: string[];
  forbidden_by_version: string[];
  benign_literals: number[];
  benign_arg_functions: string[];
  benign_arg_max: number;
  max_formula_chars: number;
  max_nesting: number;
  max_if_depth: number;
  plus_chain_terms: number;
}

function refill<T>(set: Set<T>, values: T[]): void {
  set.clear();
  for (const v of values) set.add(v);
}

function current(): FormulaRules {
  return {
    version: 'built-in', doc: CONVENTIONS_DOC,
    allowed: [...ALLOWED], justify_required: [...JUSTIFY_REQUIRED], forbidden_by_version: [...FORBIDDEN_BY_VERSION],
    benign_literals: [...BENIGN_LITERALS], benign_arg_functions: [...BENIGN_ARG_FUNCS], benign_arg_max: BENIGN_ARG_MAX,
    max_formula_chars: MAX_FORMULA_LENGTH, max_nesting: MAX_NESTING, max_if_depth: MAX_IF_DEPTH, plus_chain_terms: PLUS_CHAIN_TERMS,
  };
}

/** The lists this file ships with: what a backend that serves no rules keeps. */
const BUILT_IN: FormulaRules = current();

function isRules(r: unknown): r is FormulaRules {
  const x = r as Partial<FormulaRules> | null;
  return !!x && Array.isArray(x.allowed) && x.allowed.length > 0 && Array.isArray(x.justify_required)
    && Array.isArray(x.forbidden_by_version) && Array.isArray(x.benign_literals) && Array.isArray(x.benign_arg_functions)
    && [x.benign_arg_max, x.max_formula_chars, x.max_nesting, x.max_if_depth, x.plus_chain_terms].every((n) => typeof n === 'number');
}

/** Lint by the server's rules from now on. A malformed answer is ignored: the lint keeps the rules it had. Returns
 *  whether the rules were applied. The refusal names the server's document (`not in the allow-list (<doc> §2)`), as
 *  the server's own refusal does. */
export function applyFormulaRules(rules: unknown): boolean {
  if (!isRules(rules)) return false;
  refill(ALLOWED, rules.allowed.map((f) => f.toUpperCase()));
  refill(JUSTIFY_REQUIRED, rules.justify_required.map((f) => f.toUpperCase()));
  refill(FORBIDDEN_BY_VERSION, rules.forbidden_by_version.map((f) => f.toUpperCase()));
  refill(BENIGN_LITERALS, rules.benign_literals);
  refill(BENIGN_ARG_FUNCS, rules.benign_arg_functions.map((f) => f.toUpperCase()));
  BENIGN_ARG_MAX = rules.benign_arg_max;
  MAX_FORMULA_LENGTH = rules.max_formula_chars;
  MAX_NESTING = rules.max_nesting;
  MAX_IF_DEPTH = rules.max_if_depth;
  PLUS_CHAIN_TERMS = rules.plus_chain_terms;
  CONVENTIONS_DOC = rules === BUILT_IN ? BUILT_IN.doc : `${rules.doc} §2`;
  return true;
}

/** Back to the built-in rules (sign-out, tests). */
export function resetFormulaRules(): void {
  applyFormulaRules(BUILT_IN);
}
